import { useEffect, useId, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useUser, type User } from '@mfox/shared/contexts/UserContext'
import { accountLines, initials, ROLE_LABELS } from './accountLines'
import { useTheme } from '@mfox/shared/lib/useTheme'
import { ThemeIcon, ThemeToggle } from '@mfox/shared/ui/ThemeToggle'
import './AccountMenu.css'

/** The round initials badge, as in the account menu's button. */
export function AccountAvatar({ user }: { user: User }) {
  return (
    <span className="account-avatar" aria-hidden="true">
      {initials(user.email)}
    </span>
  )
}

export interface AccountItemsProps {
  /** The signed-in user's profile page, when their role has one. */
  profileTo?: string
  /** Called after an item is chosen, so a menu or sheet holding them can close. */
  onChoose?: () => void
  className?: string
}

/** Profile, the theme switch and sign out: the account menu's items, also listed in the phone's More sheet. */
export function AccountItems({ profileTo, onChoose, className }: AccountItemsProps) {
  const { logout } = useUser()
  const { theme, toggleTheme } = useTheme()
  const navigate = useNavigate()

  const signOut = () => {
    onChoose?.()
    logout()
    navigate('/login')
  }

  return (
    <ul className={['account-items', className].filter(Boolean).join(' ')}>
      {profileTo ? (
        <li>
          <Link to={profileTo} className="account-items__item" onClick={onChoose}>Profile</Link>
        </li>
      ) : null}
      <li>
        <button type="button" className="account-items__item" onClick={toggleTheme}>
          <ThemeIcon theme={theme} />
          {theme === 'light' ? 'Dark theme' : 'Light theme'}
        </button>
      </li>
      <li>
        <button type="button" className="account-items__item" onClick={signOut}>Sign out</button>
      </li>
    </ul>
  )
}

export interface AccountMenuProps {
  /** The signed-in user's profile page, when their role has one. */
  profileTo?: string
  /** Shows the name and the goal or role beside the avatar, as at the foot of the laptop sidebar. */
  showName?: boolean
}

/**
 * The account control: an avatar that opens the profile link, the theme switch and sign out. Signed out there is no
 * account, so it is just the theme switch.
 */
export function AccountMenu({ profileTo, showName = false }: AccountMenuProps) {
  const { currentUser } = useUser()
  const { pathname } = useLocation()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const panelId = useId()

  // Close on navigation (the profile link), on a click elsewhere and on Escape.
  useEffect(() => setOpen(false), [pathname])
  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setOpen(false)
      buttonRef.current?.focus()
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  if (!currentUser) return <ThemeToggle />
  const { name, detail } = accountLines(currentUser)

  return (
    <div className={`account-menu${showName ? ' account-menu--named' : ''}`} ref={rootRef}>
      <button
        ref={buttonRef}
        type="button"
        className="account-menu__button"
        aria-label="Account menu"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((value) => !value)}
      >
        <AccountAvatar user={currentUser} />
        {showName ? (
          <span className="account-menu__lines" aria-hidden="true">
            <span className="account-menu__name">{name}</span>
            <span className="account-menu__detail">{detail}</span>
          </span>
        ) : null}
      </button>
      <div id={panelId} className="account-menu__panel" hidden={!open}>
        <div className="account-menu__who">
          <span className="account-menu__email" title={currentUser.email}>{currentUser.email}</span>
          <span className="account-menu__role">{ROLE_LABELS[currentUser.role]}</span>
        </div>
        <AccountItems profileTo={profileTo} onChoose={() => setOpen(false)} />
      </div>
    </div>
  )
}
